import { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, 
  Users, 
  Building2, 
  Trophy, 
  Home,
  Check,
  Shield,
  UserCog,
  Clipboard
} from 'lucide-react';
import { useRole, type ViewContext } from '../../contexts/RoleContext';

const roleIcons = {
  head_coach: UserCog,
  assistant_coach: Users,
  manager: Clipboard,
  athlete: Users,
  owner: Shield,
  admin: Shield,
  staff: Users,
  director: Trophy,
  table_worker: Clipboard,
  referee: Shield,
  volunteer: Users,
};

const roleLabels: Record<string, string> = {
  head_coach: 'Head Coach',
  assistant_coach: 'Assistant Coach',
  manager: 'Manager',
  athlete: 'Athlete',
  owner: 'Owner',
  admin: 'Admin',
  staff: 'Staff',
  director: 'Director',
  table_worker: 'Table Worker',
  referee: 'Referee',
  volunteer: 'Volunteer',
};

export function RoleSwitcher() {
  const {
    activeContext,
    setActiveContext,
    availableTeams,
    availableOrganizations,
    availableTournaments,
    currentOrganization,
    loading,
    hasAnyManagementRole,
  } = useRole();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="flex w-full max-w-full items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-sm font-medium text-gray-500">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  const getCurrentLabel = () => {
    switch (activeContext.type) {
      case 'spectator':
        return 'Personal';
      case 'coach':
        return activeContext.teamName;
      case 'organizer':
        return currentOrganization?.name ?? activeContext.organizationName;
      case 'tournament_staff':
        return activeContext.tournamentName;
    }
  };

  const getCurrentIcon = () => {
    switch (activeContext.type) {
      case 'spectator':
        return Home;
      case 'coach':
        return Users;
      case 'organizer':
        return Building2;
      case 'tournament_staff':
        return Trophy;
    }
  };

  const getCurrentSubLabel = () => {
    switch (activeContext.type) {
      case 'spectator':
        return 'Your personal dashboard';
      case 'coach':
        return roleLabels[activeContext.role] || activeContext.role;
      case 'organizer':
        return roleLabels[activeContext.role] || activeContext.role;
      case 'tournament_staff':
        return roleLabels[activeContext.role] || activeContext.role;
    }
  };

  const handleSelect = (context: ViewContext) => {
    setActiveContext(context);
    setIsOpen(false);
  };

  const isSelected = (context: ViewContext) => {
    if (activeContext.type !== context.type) return false;
    switch (activeContext.type) {
      case 'spectator':
        return true;
      case 'coach':
        return context.type === 'coach' && activeContext.teamId === context.teamId;
      case 'organizer':
        return context.type === 'organizer' && activeContext.organizationId === context.organizationId;
      case 'tournament_staff':
        return context.type === 'tournament_staff' && activeContext.tournamentId === context.tournamentId;
    }
  };

  const IconComponent = getCurrentIcon() as React.ComponentType<any>;

  return (
    <div className="relative w-full max-w-full" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full max-w-full items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700"
      >
        <IconComponent className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div className="flex-1 text-left truncate">
          <div className="truncate">{getCurrentLabel()}</div>
          <div className="text-xs text-gray-500 font-normal truncate">{getCurrentSubLabel()}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-0 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 max-h-[70vh] overflow-y-auto">
          {/* Spectator option */}
          <button
            onClick={() => handleSelect({ type: 'spectator' })}
            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
              isSelected({ type: 'spectator' }) ? 'bg-teal-50' : ''
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-4 h-4 text-gray-500" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-gray-900">Personal</div>
              <div className="text-xs text-gray-500">Your personal dashboard</div>
            </div>
            {isSelected({ type: 'spectator' }) && (
              <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
            )}
          </button>

          {/* Organizations */}
          {availableOrganizations.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-y border-gray-100 mt-2">
                Organizations
              </div>
              {availableOrganizations.map((org) => {
                const context: ViewContext = {
                  type: 'organizer',
                  organizationId: org.id,
                  organizationName: org.name,
                  role: org.role,
                };
                const RoleIcon = roleIcons[org.role] || Building2;
                return (
                  <button
                    key={org.id}
                    onClick={() => handleSelect(context)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected(context) ? 'bg-teal-50' : ''
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {org.logoUrl ? (
                        <img src={org.logoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-4 h-4 text-violet-600" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium text-gray-900 truncate">{org.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <RoleIcon className="w-3 h-3" />
                        {roleLabels[org.role] || org.role}
                      </div>
                    </div>
                    {isSelected(context) && (
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </>
          )}

          {/* Teams */}
          {availableTeams.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-y border-gray-100 mt-2">
                Teams
              </div>
              {availableTeams.map((team) => {
                const context: ViewContext = {
                  type: 'coach',
                  teamId: team.id,
                  teamName: team.name,
                  role: team.role,
                };
                const RoleIcon = roleIcons[team.role] || Users;
                return (
                  <button
                    key={team.id}
                    onClick={() => handleSelect(context)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected(context) ? 'bg-teal-50' : ''
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {team.logoUrl ? (
                        <img src={team.logoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium text-gray-900 truncate">{team.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <RoleIcon className="w-3 h-3" />
                        {roleLabels[team.role] || team.role}
                      </div>
                    </div>
                    {isSelected(context) && (
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </>
          )}

          {/* Active Tournaments */}
          {availableTournaments.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-y border-gray-100 mt-2">
                Active Tournaments
              </div>
              {availableTournaments.map((tournament) => {
                const context: ViewContext = {
                  type: 'tournament_staff',
                  tournamentId: tournament.id,
                  tournamentName: tournament.name,
                  role: tournament.role,
                };
                const RoleIcon = roleIcons[tournament.role] || Trophy;
                return (
                  <button
                    key={tournament.id}
                    onClick={() => handleSelect(context)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected(context) ? 'bg-teal-50' : ''
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium text-gray-900 truncate">{tournament.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <RoleIcon className="w-3 h-3" />
                        {roleLabels[tournament.role] || tournament.role}
                        {tournament.organizationName && (
                          <span className="text-gray-400">• {tournament.organizationName}</span>
                        )}
                      </div>
                    </div>
                    {isSelected(context) && (
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Compact version for mobile nav
export function RoleSwitcherCompact() {
  const {
    activeContext,
    setActiveContext,
    availableTeams,
    availableOrganizations,
    availableTournaments,
    loading,
    hasAnyManagementRole,
  } = useRole();

  const [isOpen, setIsOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
      </div>
    );
  }

  const getCurrentIcon = () => {
    switch (activeContext.type) {
      case 'spectator':
        return Home;
      case 'coach':
        return Users;
      case 'organizer':
        return Building2;
      case 'tournament_staff':
        return Trophy;
    }
  };

  const handleSelect = (context: ViewContext) => {
    setActiveContext(context);
    setIsOpen(false);
  };

  const IconComponent = getCurrentIcon() as React.ComponentType<any>;
  const totalContexts = 1 + availableTeams.length + availableOrganizations.length + availableTournaments.length;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <IconComponent className="w-5 h-5 text-gray-600" />
        {totalContexts > 1 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {totalContexts}
          </span>
        )}
      </button>

      {/* Mobile modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => setIsOpen(false)}>
          <div 
            className="w-full max-w-lg bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Switch View</h2>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-2">
              {/* Spectator */}
              <button
                onClick={() => handleSelect({ type: 'spectator' })}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeContext.type === 'spectator' ? 'bg-teal-50 border-2 border-teal-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium">Personal</div>
                  <div className="text-sm text-gray-500">Your personal dashboard</div>
                </div>
              </button>

              {/* Organizations */}
              {availableOrganizations.length > 0 && (
                <div className="mt-4">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Organizations</div>
                  {availableOrganizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleSelect({
                        type: 'organizer',
                        organizationId: org.id,
                        organizationName: org.name,
                        role: org.role,
                      })}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        activeContext.type === 'organizer' && activeContext.organizationId === org.id
                          ? 'bg-teal-50 border-2 border-teal-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center overflow-hidden">
                        {org.logoUrl ? (
                          <img src={org.logoUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Building2 className="w-5 h-5 text-violet-600" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{org.name}</div>
                        <div className="text-sm text-gray-500">{roleLabels[org.role]}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Teams */}
              {availableTeams.length > 0 && (
                <div className="mt-4">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Teams</div>
                  {availableTeams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => handleSelect({
                        type: 'coach',
                        teamId: team.id,
                        teamName: team.name,
                        role: team.role,
                      })}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        activeContext.type === 'coach' && activeContext.teamId === team.id
                          ? 'bg-teal-50 border-2 border-teal-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center overflow-hidden">
                        {team.logoUrl ? (
                          <img src={team.logoUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{team.name}</div>
                        <div className="text-sm text-gray-500">{roleLabels[team.role]}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Tournaments */}
              {availableTournaments.length > 0 && (
                <div className="mt-4 mb-4">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Active Tournaments</div>
                  {availableTournaments.map((tournament) => (
                    <button
                      key={tournament.id}
                      onClick={() => handleSelect({
                        type: 'tournament_staff',
                        tournamentId: tournament.id,
                        tournamentName: tournament.name,
                        role: tournament.role,
                      })}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        activeContext.type === 'tournament_staff' && activeContext.tournamentId === tournament.id
                          ? 'bg-teal-50 border-2 border-teal-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{tournament.name}</div>
                        <div className="text-sm text-gray-500">
                          {roleLabels[tournament.role]}
                          {tournament.organizationName && ` • ${tournament.organizationName}`}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

